// Copyright ©️ 2026 eVolve MEP, LLC

import { FLAT_BLACK_FILLER, FLAT_WHITE_FILLER } from '@evolvemep/api-ts';

import textboxSvg from 'assets/icons/icon-src/markup/textbox.svg?raw';
import {
  type MarkupToolContext,
  type PanelConfig,
  BaseMarkupTool,
} from 'components/Autodesk/extensions/MarkupModeExtension/BaseMarkupTool';

const { Autodesk } = window;

export class TextBoxTool extends BaseMarkupTool {
  readonly type = 'label'; // Matches the existing type used by Autodesk's text markup tool, which we build on top of.

  constructor(context: MarkupToolContext) {
    super(context, {
      strokeColor: FLAT_BLACK_FILLER, // Font color.
      strokeOpacity: 100, // Font opacity.
      fillColor: FLAT_WHITE_FILLER, // Background color.
      fillOpacity: 50, // Background opacity.
      fontSize: 16,
      fontFamily: 'arial',
      isBold: false,
      isItalic: false,
    });
  }

  protected getPanelConfig(): PanelConfig {
    return {
      panelTitle: 'Text Box',
      sections: [
        {
          heading: 'Font',
          fields: [
            { property: 'font-size', label: 'Size' },
            { property: 'font-family', label: 'Family' },
            { property: 'stroke-color', colorLabel: 'Color', opacityLabel: 'Opacity' },
            { property: 'font-style', label: 'Style' },
          ],
        },
        {
          heading: 'Fill',
          fields: [{ property: 'fill-color', colorLabel: 'Color', opacityLabel: 'Opacity' }],
        },
      ],
    };
  }

  saveChangesIfEditing() {
    if (this.markupExtension.editMode.type !== this.type) return;
    const textInputHelper = this.markupExtension.editMode.textInputHelper;
    if (!textInputHelper || !textInputHelper.isActive() || textInputHelper.textMarkup.currentText.length === 0) return;
    textInputHelper.acceptAndExit();
  }

  override onMarkupSelected() {
    this.saveChangesIfEditing();
    super.onMarkupSelected();
  }

  override activate() {
    this.saveChangesIfEditing();
    super.activate();
  }

  override deactivate() {
    this.saveChangesIfEditing();
    super.deactivate();
  }

  protected activateEditMode() {
    const textEditMode = new Autodesk.Viewing.Extensions.Markups.Core.EditModeText(this.markupExtension);
    this.markupExtension.changeEditMode(textEditMode);
  }

  protected readonly buttonIcon = textboxSvg;
  protected readonly buttonTooltip = 'Text Box';
}
